import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../../providers/AuthProvider';
import User from '../../../models/User';
import Modal from '../../../components/Modal';
import ReferralModal from '../../talent/ReferralModal';
import ApplicationStepper from '../components/ApplicationStepper';
import TalentStatusLayout from './TalentStatusLayout';
import JourneyList, { JourneyActions, JourneyFoot, sectionRows } from './JourneyList';
import {
    formatMinutes,
    getJourneyProgress,
    getOptionalSuggestions,
    markTalentStatusSeen,
} from '../components/applicationProgress';

/**
 * Which "where you are" screen a talent user should see.
 * Mirrors the application → profile journey in Auth.appropriate().
 */
export const resolveTalentStatus = (user) => {
    if (!user) return null;
    if (user.isApplicationRejected()) return 'notApproved';
    if (user.isBeingReviewed()) return 'appUnderReview';
    if (!user.isApplicationApproved()) return 'appInProgress';
    if (user.isProfileApproved()) return null;
    // A pending appeal wins over the original rejection (both flags stay set)
    if (user.isProfilePendingAppeal()) return 'profileUnderReview';
    if (user.isProfileRejected() || user.isProfileAppealRejected()) return 'changesRequested';
    if (user.isPendingProfileApproved()) return 'profileUnderReview';
    if (user.isProfileReadyForApproval()) return 'profileReady';
    return 'profileInProgress';
};

const PROFILE_REVIEW = '1–2 business days';
const APPLICATION_REVIEW = '24–72 hours';

const btnPrimary = 'btn btn-primary inline-flex items-center justify-center gap-2 px-6';
const btnSecondary = 'btn btn-outline-dark px-6';
const linkButton = 'bg-transparent border-0 p-0 text-gray-500 underline hover:text-gray-900';

/**
 * Login status screens (application in progress / under review / not
 * approved, and the profile-phase equivalents). Rendered on the routes
 * Auth.appropriate() sends talent to after logging in.
 *
 * @param {string} variant  Force a variant (e.g. 'approvedNotStarted' on /talent/welcome)
 */
const TalentStatusPage = ({ variant: forcedVariant }) => {
    const { auth, logout, markTalentWelcomeAsViewed } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const referralModal = useRef(null);
    const referralForm = useRef(null);
    const [busy, setBusy] = useState(false);

    const user = useMemo(() => (auth ? User.make({ ...auth }) : null), [auth]);
    const variant = forcedVariant || resolveTalentStatus(user);
    const isProfilePhase = !!user?.isApplicationApproved?.();

    const progress = useMemo(() => {
        if (!user) return null;
        const steps = isProfilePhase ? user.getFullProfileStepsForTalent() : user.getApplicationStepsForTalent();
        return getJourneyProgress(user, [...steps]);
    }, [user, isProfilePhase]);

    // Profile-phase screens show once per login. Marked as seen when the talent
    // leaves the screen (not on mount), so the post-login auth refresh that
    // re-runs Auth.appropriate() doesn't bounce them past it.
    useEffect(() => {
        if (!user || !isProfilePhase || variant === 'approvedNotStarted') return;
        return () => markTalentStatusSeen(user);
    }, [user?.id, variant]);

    // "Refer a colleague" link from the application-submitted pop-up
    useEffect(() => {
        if (location.state?.refer) referralModal.current?.showModal();
    }, []);

    if (!user || !variant) {
        return <Navigate to="/talent/dashboard" replace />;
    }

    const first = user.first_name;
    const remaining = progress.requiredRemaining;
    const next = remaining[0];
    const stepPath = (section) => `/talent/application${section?.path || ''}`;

    const toDashboard = async () => {
        if (variant === 'approvedNotStarted') await markTalentWelcomeAsViewed();
        navigate('/talent/dashboard', { replace: true });
    };
    const continueTo = async (path) => {
        setBusy(true);
        if (variant === 'approvedNotStarted') await markTalentWelcomeAsViewed();
        navigate(path, { replace: true });
    };

    const dashboardLink = (
        <button type="button" className={linkButton} disabled={busy} onClick={toDashboard}>
            Not now — go to my dashboard
        </button>
    );
    const logoutLink = (
        <button type="button" className={linkButton} onClick={() => logout()}>Log out</button>
    );
    const leftToDo = {
        title: 'Left to do',
        meta: progress.minutesLeft ? `~${formatMinutes(progress.minutesLeft)}` : null,
        rows: sectionRows(remaining),
    };
    const profileSteps = [
        { kind: 'stepnow', label: 'Submit for approval', meta: 'now' },
        { kind: 'step', label: 'Wripple reviews your profile', meta: PROFILE_REVIEW },
        { kind: 'step', label: 'Go live to clients' },
    ];

    let card;
    switch (variant) {
        case 'appInProgress':
            card = {
                eyebrow: 'Welcome back',
                title: `Pick up your application, ${first}`,
                subtitle: next
                    ? <><b className="text-gray-900">{progress.requiredDoneCount} of {progress.requiredCount} sections done</b> · about {formatMinutes(progress.minutesLeft)} left.</>
                    : 'Every section is done. Give it a quick review and submit.',
                list: leftToDo,
                actions: (
                    <button type="button" className={btnPrimary} disabled={busy}
                            onClick={() => continueTo(next ? stepPath(next) : '/talent/application/review')}>
                        {next ? <>Continue with {next.title}</> : 'Review & submit'} <ArrowRight className="h-4 w-4" />
                    </button>
                ),
                foot: logoutLink,
            };
            break;

        case 'appUnderReview':
            card = {
                eyebrow: 'Application submitted',
                title: "We're still reviewing your application",
                subtitle: <>Most reviews take <b className="text-gray-900">{APPLICATION_REVIEW}</b>. We'll email you either way at {user.email}.</>,
                list: {
                    title: 'What happens next',
                    rows: [
                        { kind: 'stepnow', label: 'Wripple reviews your application', meta: '24–72 hrs' },
                        { kind: 'step', label: 'Build your profile', meta: '~11 min' },
                        { kind: 'step', label: 'Go live to clients' },
                    ],
                },
                foot: (
                    <>
                        Know someone who should be on Wripple?{' '}
                        <button type="button" className={linkButton} onClick={() => referralModal.current?.showModal()}>Refer them</button>
                        {' · '}{logoutLink}
                    </>
                ),
            };
            break;

        case 'notApproved':
            card = {
                eyebrow: 'Application update',
                eyebrowTone: 'mute',
                title: "We're not moving forward right now",
                subtitle: `Thanks for applying, ${first}. Unfortunately your application to join Wripple wasn't accepted at this time.`,
                list: {
                    title: 'What you can do',
                    rows: [
                        { kind: 'dot', label: 'We generally reconsider applications every six months' },
                        { kind: 'dot', label: 'Email TalentTeam@wripple.com and we’ll restart your application' },
                    ],
                },
                foot: logoutLink,
            };
            break;

        case 'approvedNotStarted':
            card = {
                eyebrow: '✓ Application approved',
                title: `You're in, ${first}! Now let's build your profile`,
                subtitle: <><b className="text-gray-900">{progress.requiredDoneCount} of {progress.requiredCount}</b> required sections are already done from your application. Clients can't see you until the rest are done and approved.</>,
                list: leftToDo,
                actions: (
                    <>
                        <button type="button" className={btnPrimary} disabled={busy}
                                onClick={() => continueTo(next ? stepPath(next) : '/talent/application')}>
                            {next ? <>Start with {next.title}</> : 'Review my profile'} <ArrowRight className="h-4 w-4" />
                        </button>
                        {user.canImportResumeItemsForProfile?.() && (
                            <button type="button" className={btnSecondary} disabled={busy}
                                    onClick={() => navigate('/talent/welcome/import-resume')}>
                                Import from resume
                            </button>
                        )}
                    </>
                ),
                foot: dashboardLink,
                onClose: toDashboard,
            };
            break;

        case 'profileInProgress':
            card = {
                eyebrow: 'Welcome back',
                title: `Pick up where you left off, ${first}`,
                subtitle: <><b className="text-gray-900">{progress.requiredDoneCount} of {progress.requiredCount}</b> required sections done. {remaining.length === 1 ? 'One left' : `${remaining.length} left`}, then Wripple reviews within <b className="text-gray-900">{PROFILE_REVIEW}</b>.</>,
                list: leftToDo,
                actions: (
                    <button type="button" className={btnPrimary} disabled={busy}
                            onClick={() => continueTo(next ? stepPath(next) : '/talent/application')}>
                        {next ? <>Continue with {next.title}</> : 'Continue my profile'} <ArrowRight className="h-4 w-4" />
                    </button>
                ),
                foot: dashboardLink,
                onClose: toDashboard,
            };
            break;

        case 'profileReady':
            card = {
                eyebrow: 'Ready to submit',
                eyebrowTone: 'warn',
                title: 'Your profile is ready — submit it for approval',
                subtitle: <>All <b className="text-gray-900">{progress.requiredCount} of {progress.requiredCount}</b> required sections are done. Clients can't see you until Wripple approves it.</>,
                list: { title: 'What happens next', rows: profileSteps },
                actions: (
                    <>
                        <button type="button" className={btnPrimary} disabled={busy}
                                onClick={() => continueTo('/talent/application?submitForApproval=1')}>
                            Submit for approval <ArrowRight className="h-4 w-4" />
                        </button>
                        <button type="button" className={btnSecondary} disabled={busy}
                                onClick={() => continueTo('/talent/application')}>
                            Review my profile
                        </button>
                    </>
                ),
                foot: dashboardLink,
                onClose: toDashboard,
            };
            break;

        case 'profileUnderReview': {
            const optional = getOptionalSuggestions(user);
            const suggestion = optional.remaining[0];
            const isAppeal = user.isProfilePendingAppeal() && !user.isPendingProfileApproved();
            card = {
                eyebrow: isAppeal ? 'Appeal submitted' : 'Submitted for approval',
                title: isAppeal ? 'Your appeal is under review' : 'Your profile is under review',
                subtitle: isAppeal
                    ? "A talent manager will review your appeal and reach out via email. You can keep improving your profile while you wait."
                    : <>We'll email you within <b className="text-gray-900">{PROFILE_REVIEW}</b>. You can keep improving it while you wait — changes are included in the review.</>,
                list: {
                    title: 'Worth adding while you wait',
                    meta: `${optional.addedCount} of ${optional.all.length} optional added`,
                    rows: sectionRows(optional.remaining, {
                        badgeFirst: suggestion?.path === '/video-interview' ? 'Recommended' : undefined,
                    }),
                },
                actions: (
                    <>
                        {suggestion && (
                            <button type="button" className={btnPrimary} disabled={busy}
                                    onClick={() => continueTo(stepPath(suggestion))}>
                                {suggestion.path === '/video-interview' ? 'Start your video interview' : <>Add {suggestion.title}</>}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        )}
                        <button type="button" className={btnSecondary} disabled={busy}
                                onClick={() => navigate(`/talent-profiles/${user.id}`)}>
                            Preview client view
                        </button>
                    </>
                ),
                foot: dashboardLink,
                onClose: toDashboard,
            };
            break;
        }

        case 'changesRequested': {
            const notes = user.latest_appeal?.rejection_notes || user.talent_profile_rejection_notes;
            card = {
                eyebrow: 'Changes requested',
                eyebrowTone: 'warn',
                title: 'A few changes before we can approve',
                subtitle: "Update your profile with the feedback below and resubmit — clients can't see you until then.",
                list: notes ? {
                    title: 'Feedback from the Wripple talent team',
                    rows: [{ kind: 'flag', label: 'Wripple feedback', detail: notes }],
                } : null,
                actions: (
                    <button type="button" className={btnPrimary} disabled={busy}
                            onClick={() => continueTo('/talent/application')}>
                        Update my profile <ArrowRight className="h-4 w-4" />
                    </button>
                ),
                foot: dashboardLink,
                onClose: toDashboard,
            };
            break;
        }

        default:
            return <Navigate to="/talent/dashboard" replace />;
    }

    return (
        <TalentStatusLayout onClose={card.onClose}>
            <div className="hidden md:block pb-6">
                <ApplicationStepper />
            </div>

            <p className={TB.mapClass('m-0 text-[11px] md:text-xs font-bold uppercase tracking-wider', {
                'text-secondary': !card.eyebrowTone,
                'text-amber-600': card.eyebrowTone === 'warn',
                'text-gray-500': card.eyebrowTone === 'mute',
            })}>
                {card.eyebrow}
            </p>
            <h2 className="mx-auto mt-2 mb-0 max-w-lg text-xl leading-tight md:text-[28px] font-bold text-gray-900">
                {card.title}
            </h2>
            <p className="mx-auto mt-2 md:mt-3 mb-0 max-w-xl text-sm md:text-base text-gray-600">{card.subtitle}</p>

            {card.list && (
                <div className="mt-5 md:mt-7">
                    <JourneyList {...card.list} />
                </div>
            )}

            {card.actions && (
                <div className="mt-5 md:mt-7">
                    <JourneyActions>{card.actions}</JourneyActions>
                </div>
            )}

            {card.foot && (
                <div className="mt-4 md:mt-5">
                    <JourneyFoot>{card.foot}</JourneyFoot>
                </div>
            )}

            {variant === 'appUnderReview' && (
                <Modal
                    ref={referralModal}
                    modalClass="referral-modal-form"
                    onCloseAction={async () => {
                        await TB.wait(400);
                        return referralForm.current?.resetForm();
                    }}>
                    <ReferralModal ref={referralForm} onToggleDisabled={setBusy} isDisabled={busy} />
                </Modal>
            )}
        </TalentStatusLayout>
    );
};

export default TalentStatusPage;
